import { Icon, type IconName } from "@/components/icons";
import { Text } from "@/components/text";
import { Button } from "@/components/ui/button";
import { cn } from "cn";
import Link from "next/link";

type Action =
  | {
      label: string;
      type: "button";
      onClick: () => void;
    }
  | {
      label: string;
      type: "link";
      href: string;
      external?: boolean;
    };

export const EmptyState = ({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  className,
  children,
}: {
  icon: IconName | React.ReactNode;
  title: string;
  description: React.ReactNode;
  primaryAction?: Action;
  secondaryAction?: Action;
  className?: string;
  children?: React.ReactNode;
}) => {
  return (
    <section
      className={cn(
        "flex gap-3 flex-col justify-center py-12 items-center w-full",
        className,
      )}
    >
      <div className="relative size-12 grid place-items-center">
        <div className="absolute inset-0 rounded-xl bg-neutral-100 dark:bg-neutral-800" />
        <span className="relative text-neutral-500 dark:text-neutral-400 flex items-center justify-center">
          {typeof icon === "string" ? (
            <Icon name={icon as IconName} size={28} />
          ) : (
            icon
          )}
        </span>
      </div>

      <div className="text-center">
        <Text className="text-neutral-900 dark:text-neutral-100 font-semibold mb-1">
          {title}
        </Text>
        <Text className="max-w-sm text-center">{description}</Text>
      </div>

      {(primaryAction || secondaryAction || children) && (
        <div className="flex gap-4">
          {children}
          {secondaryAction &&
            (secondaryAction.type === "button" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            ) : (
              <Button asChild variant="outline" size="sm">
                <Link
                  href={secondaryAction.href}
                  {...(secondaryAction.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  {secondaryAction.label}
                </Link>
              </Button>
            ))}
          {primaryAction &&
            (primaryAction.type === "button" ? (
              <Button
                variant="primary"
                size="sm"
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </Button>
            ) : (
              <Button asChild variant="primary" size="sm">
                <Link
                  href={primaryAction.href}
                  {...(primaryAction.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  {primaryAction.label}
                </Link>
              </Button>
            ))}
        </div>
      )}
    </section>
  );
};
